/**
 * LoginPage - User authentication
 */
import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

const LoginPage = ({ onSuccess }) => {
  const { login, register, loading, error } = useAuth();
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    displayName: '',
  });
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    
    try {
      if (isRegistering) {
        if (!formData.displayName) {
          setFormError('Display name is required');
          return;
        }
        if (formData.password.length < 8) {
          setFormError('Password must be at least 8 characters');
          return;
        }
        await register(formData.email, formData.password, formData.displayName);
      } else {
        await login(formData.email, formData.password);
      }
      onSuccess?.();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="login-page" data-testid="login-page">
      <div className="login-container">
        <div className="login-header">
          <img src="/riskee_logo.png" alt="RISKEE" className="login-logo" />
          <h1>RISKEE</h1>
          <p>Workplace Health & Safety</p>
        </div>

        <div className="login-card">
          <h2>{isRegistering ? 'Create Account' : 'Sign In'}</h2>
          
          {(formError || error) && (
            <div className="login-error" data-testid="login-error">
              {formError || error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form">
            {isRegistering && (
              <div className="input-group">
                <label htmlFor="displayName">Full Name</label>
                <input
                  type="text"
                  id="displayName"
                  name="displayName"
                  value={formData.displayName}
                  onChange={handleChange}
                  placeholder="John Smith"
                  required={isRegistering}
                  data-testid="input-display-name"
                />
              </div>
            )}

            <div className="input-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                data-testid="input-email"
              />
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={isRegistering ? 'Min 8 characters' : '••••••••'}
                required
                data-testid="input-password"
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary btn-lg btn-block"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? 'Please wait...' : (isRegistering ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="login-footer">
            <button 
              type="button"
              className="btn-link"
              onClick={() => {
                setIsRegistering(!isRegistering);
                setFormError('');
              }}
              data-testid="toggle-mode-btn"
            >
              {isRegistering 
                ? 'Already have an account? Sign in' 
                : "Don't have an account? Register"}
            </button>
          </div>

          {/* Demo credentials hint */}
          <div className="demo-hint">
            <p><strong>Demo Credentials:</strong></p>
            <p>
              <code>demo@riskee.com.au</code><br/>
              Password: <code>Riskee_!2025</code>
            </p>
            <p className="enterprise-note">
              Self-hosted and enterprise deployments use your organisation login.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
